import { Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { ColumnConfig } from '@omnea-app/fe-tables/columns/useColumnConfig'
import { FlaggedScoreChip } from '@omnea-app/frontend/components/shared/flagged-score/FlaggedScoreChip'
import { SummaryRiskLevel } from '@omnea-app/risks/risksFrontendClient'
import { useTprmSummaryLevelColumns } from './useTprmSummaryLevelColumns'

type Input = {
  sortable?: boolean
}

type SupportedRow = {
  risks: SummaryRiskLevel[]
  inherentRisk: number | null
  residualRisk: number | null
  riskCount: number | null
  riskScore: number | null
} & ({ id: string } | { requestId: string })

const riskCountValueGetter = <T extends SupportedRow>(_: unknown, row: T) => row.riskCount ?? 0
const riskScoreValueGetter = <T extends SupportedRow>(_: unknown, row: T) => row.riskScore ?? 0

export const useRequestRiskColumns = <T extends SupportedRow>({ sortable }: Input): ColumnConfig<T>[] => {
  const { TPRM } = useFeatureFlags()
  const { t } = useTranslation('requests')
  const tprmRiskColumns = useTprmSummaryLevelColumns<T>({
    aggregationEntity: 'request',
    getViewRisksUrl: (params) => `/requests/detail/${'requestId' in params.row ? params.row.requestId : params.row.id}/risks`,
  })

  const autoFlaggedColumns: ColumnConfig<T>[] = [
    {
      columnType: 'custom-render',
      field: 'riskCount' as Extract<keyof T, string>,
      type: 'number',
      sortable,
      headerName: t('request-risk.flagged-risks'),
      valueGetter: riskCountValueGetter,
      renderCell: (params) => {
        if (!params.row.riskCount || params.row.riskCount <= 0) return null
        return (
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" width="100%">
            <FlaggedScoreChip score={params.row.riskCount} tooltip="questions-count" />
          </Stack>
        )
      },
    },
    {
      columnType: 'custom-render',
      field: 'riskScore' as Extract<keyof T, string>,
      sortable,
      headerName: t('request-risk.risk-score'),
      valueGetter: riskScoreValueGetter,
      renderCell: (params) => {
        if (!params.row.riskScore || params.row.riskScore <= 0) return null
        return (
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" width="100%">
            <FlaggedScoreChip score={params.row.riskScore} tooltip="aggregate-request" icon="flag" color="warning" />
          </Stack>
        )
      },
    },
  ]

  return TPRM ? [...autoFlaggedColumns, ...tprmRiskColumns] : autoFlaggedColumns
}
